import { ServerActionResult } from '@/shared/types/server-action.type';
import { refreshToken, signOut } from '@/features/auth/actions/auth-actions';

// Error codes that trigger token refresh (from TabID backend)
const REFRESH_TRIGGER_CODES = ['AUTHENTICATION_REQUIRED'];

function shouldRefreshToken(errorCode: string | undefined): boolean {
	return !!errorCode && REFRESH_TRIGGER_CODES.includes(errorCode);
}

/**
 * Handle session expired - sign out and redirect to login
 */
async function handleSessionExpired(): Promise<void> {
	await signOut();
	window.location.href = '/login';
}

/**
 * Wraps a server action with automatic token refresh and retry on 401 errors.
 *
 * @example
 * const { mutateAsync } = useSafeMutation({
 *   action: withAuth(createProject),
 *   onSuccess: () => toast.success('Created!'),
 * });
 */
// Overload for queries (no variables)
export function withAuth<TData>(
	action: () => Promise<ServerActionResult<TData>>
): () => Promise<ServerActionResult<TData>>;

// Overload for mutations (with variables)
export function withAuth<TData, TVariables>(
	action: (variables: TVariables) => Promise<ServerActionResult<TData>>
): (variables: TVariables) => Promise<ServerActionResult<TData>>;

// Implementation
export function withAuth<TData, TVariables = void>(
	action: (variables?: TVariables) => Promise<ServerActionResult<TData>>
): (variables?: TVariables) => Promise<ServerActionResult<TData>> {
	return async (variables?: TVariables) => {
		let result = await action(variables);

		// Handle 401 - refresh and retry once
		if (!result.success && shouldRefreshToken(result.error?.code)) {
			const refreshResult = await refreshToken();

			if (refreshResult.success) {
				// Retry original action with new token
				result = await action(variables);
			} else {
				// Refresh failed - sign out and redirect to login
				await handleSessionExpired();
			}
		}

		return result;
	};
}
